import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, FileText, Loader2 } from "lucide-react";
import { API, api, formatApiError } from "../../api";
import { formatCurrency } from "../../lib/format";
import { useCelebration } from "../../contexts/CelebrationContext";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { FormField } from "../common/FormField";
import { toast } from "../ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const PAYMENT_MODES = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque"];

// Step 2 ("Membership & payment") shared by the Walk-in "New member" flow (mandatory — a
// walk-in isn't a paid member without it), the global "+New member" dialog, and the Add-family
// "brand-new person" branch (skippable — a family member can be linked/created first and get a
// plan later via the existing Add-plan flow). Three internal phases: pick a plan
// (-> POST /memberships), collect the first payment (-> POST /payments), then a brief "payment
// collected" phase with a Generate/Download invoice option — per explicit product decision, ANY
// persona creating a new member (Walk-in/Add-family/global dialog all route through this ONE
// shared component) must be offered an invoice right here, not just later from Member Detail's
// ledger. `onComplete` only fires once the user taps Continue past that success phase.
export function MembershipPaymentStep({ member, branchId, allowSkip, onSkip, onComplete, testIds, initialPlanId }) {
  const { celebrate } = useCelebration();
  const [phase, setPhase] = useState("plan");
  const [plan, setPlan] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [discountPercent, setDiscountPercent] = useState("0");
  const [membership, setMembership] = useState(null);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [amountRupees, setAmountRupees] = useState("");
  const [txnRef, setTxnRef] = useState("");
  const [lastPayment, setLastPayment] = useState(null);

  const plansQ = useQuery({ queryKey: ["desk-plans"], queryFn: async () => (await api.get("/plans")).data.plans });
  const maxDiscount = plan?.max_discount_percent ?? 100;

  // A lead converted with a "Plan Interest" on file arrives here with `initialPlanId` — pre-
  // select it once plans have loaded so staff don't have to re-pick what the lead already told
  // them they wanted, but it stays a normal, fully-editable Select (not locked in).
  useEffect(() => {
    if (initialPlanId && !plan && plansQ.data) {
      const match = plansQ.data.find((p) => p.id === initialPlanId);
      if (match) setPlan(match);
    }
  }, [initialPlanId, plan, plansQ.data]);

  const createMembershipMutation = useMutation({
    mutationFn: () => api.post(
      "/memberships",
      { member_ref: member.id, plan_ref: plan.id, start: startDate, discount_percent: Number(discountPercent) || 0 },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: (res) => { setMembership(res.data); setAmountRupees(String(res.data.billing.amount_due / 100)); setPhase("payment"); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const paymentMutation = useMutation({
    mutationFn: () => api.post(
      "/payments",
      {
        mode: paymentMode === "Cash" ? "cash" : "digital", payment_method: paymentMode,
        amount: Math.round(Number(amountRupees) * 100), branch_id: branchId,
        member_ref: member.id, membership_ref: membership.id, rail_ref: paymentMode !== "Cash" ? txnRef : null,
      },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: (res) => {
      celebrate("payment_collected", `${formatCurrency(res.data.amount)} — ${plan?.name} activated for ${member?.profile?.name}.`);
      setLastPayment(res.data);
      setPhase("success");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const [invoiceId, setInvoiceId] = useState(null);
  const invoiceMutation = useMutation({
    mutationFn: () => api.post("/invoices/for-payment", { payment_ref: lastPayment.id }),
    onSuccess: (res) => setInvoiceId(res.data.id),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const amountDue = membership?.billing?.amount_due ?? 0;
  const outstanding = amountDue - Math.round((Number(amountRupees) || 0) * 100);

  if (phase === "plan") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Plan <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
          <Select onValueChange={(v) => { setPlan(plansQ.data.find((p) => p.id === v)); setDiscountPercent("0"); }} value={plan?.id}>
            <SelectTrigger data-testid={testIds.planSelect}><SelectValue placeholder={plansQ.isLoading ? "Loading plans…" : "Choose a plan"} /></SelectTrigger>
            <SelectContent>
              {plansQ.data?.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.pricing.amount)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {plan && (
          <p className="text-xs" style={{ color: "#68686E" }}>
            Plan price {formatCurrency(plan.pricing.amount)} + joining fee {formatCurrency(plan.pricing.joining_fee || 0)}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start date" type="date" testId={testIds.startDateInput} value={startDate} onChange={setStartDate} helperText="Can be backdated" />
          {plan && (
            <div className="space-y-1.5">
              <Label>Discount % <span className="text-xs font-normal" style={{ color: "#68686E" }}>(max {maxDiscount}%)</span></Label>
              <FormField
                type="number" testId={testIds.discountInput} value={discountPercent}
                onChange={(v) => setDiscountPercent(v === "" ? "" : String(Math.min(Number(v) || 0, maxDiscount)))}
              />
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          {allowSkip && (
            <Button variant="outline" data-testid={testIds.skipButton} onClick={onSkip}>Skip for now</Button>
          )}
          <Button
            data-testid={testIds.planNextButton}
            disabled={!plan || createMembershipMutation.isPending}
            onClick={() => createMembershipMutation.mutate()}
            className="text-white flex-1" style={{ background: "#D6202B" }}
          >
            {createMembershipMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : "Continue to payment"}
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div className="space-y-3">
        <div className="rounded-[10px] p-4" style={{ background: "var(--status-active-bg, rgba(52, 199, 89, 0.1))" }}>
          <p className="text-sm font-semibold" style={{ color: "#141416" }}>
            Payment collected — {formatCurrency(lastPayment?.amount ?? 0)}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{plan?.name} activated for {member?.profile?.name}.</p>
        </div>
        {invoiceId ? (
          <div className="flex gap-2">
            <Button data-testid={`${testIds.invoiceButton}-show`} variant="outline" onClick={() => window.open(`${API}/invoices/${invoiceId}/pdf?inline=true`, "_blank")} className="flex-1 gap-1.5">
              <Eye size={15} /> Show invoice
            </Button>
            <Button data-testid={testIds.invoiceButton} variant="outline" onClick={() => window.open(`${API}/invoices/${invoiceId}/pdf`, "_blank")} className="flex-1 gap-1.5">
              <FileText size={15} /> Download
            </Button>
          </div>
        ) : (
          <Button
            data-testid={testIds.invoiceButton}
            variant="outline"
            disabled={invoiceMutation.isPending}
            onClick={() => invoiceMutation.mutate()}
            className="w-full gap-1.5"
          >
            {invoiceMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Generate invoice
          </Button>
        )}
        <Button
          data-testid={testIds.continueButton}
          onClick={() => onComplete({ membership, plan, payment: lastPayment })}
          className="w-full text-white" style={{ background: "#D6202B" }}
        >
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm" data-testid={testIds.netPayable} style={{ color: "#141416" }}>
        Net payable: <span className="font-semibold">{formatCurrency(amountDue)}</span>
        {membership?.discount_percent > 0 && <span className="ml-1 text-xs" style={{ color: "var(--scarlet-pos)" }}>({membership.discount_percent}% off applied)</span>}
      </p>
      <div className="space-y-1.5">
        <Label>Payment mode</Label>
        <Select onValueChange={setPaymentMode} value={paymentMode}>
          <SelectTrigger data-testid={testIds.paymentMode}><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <FormField
        label="Amount (₹)" required type="number" testId={testIds.paymentAmount} value={amountRupees}
        onChange={(v) => setAmountRupees(v === "" ? "" : String(Math.min(Number(v) || 0, amountDue / 100)))}
        helperText={`Up to ${formatCurrency(amountDue)} net payable — excess amounts aren't accepted here.`}
      />
      {paymentMode !== "Cash" && (
        <FormField label="Transaction / reference number" required testId={testIds.txnRefInput} value={txnRef} onChange={setTxnRef} />
      )}
      {outstanding > 0 && (
        <p className="text-xs font-medium" data-testid={testIds.outstandingBalance} style={{ color: "var(--scarlet-neg)" }}>
          {formatCurrency(outstanding)} will remain outstanding on this membership.
        </p>
      )}
      <Button
        data-testid={testIds.paymentSubmit}
        disabled={!amountRupees || (paymentMode !== "Cash" && !txnRef) || paymentMutation.isPending}
        onClick={() => paymentMutation.mutate()}
        className="w-full text-white" style={{ background: "#D6202B" }}
      >
        {paymentMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : `Collect ${amountRupees ? formatCurrency(Math.round(Number(amountRupees) * 100)) : ""}`}
      </Button>
    </div>
  );
}
